import { Box, Tooltip } from '@mui/material';
import { OPERATIONS_SUPERVISOR } from '@globalConstants';
import { t } from '@i18n';
import { Chip } from '@molecules';
import { formatDate } from '@utils';
import { PendingCustomer, PendingCustomerStatus } from './data/useGetPendingCustomers';
import { SelectedPendingCustomer } from './data/useResolvePendingCustomer/hooks/useResolvePendingCustomer';
import ActionButtons from './modules/ActionButtons';
import styles from './styles';

export const DEFAULT_ROWS_PER_PAGE = 10;

export const STATUS_OPTIONS: { value: PendingCustomerStatus; label: string }[] = [
  { value: 'PENDING', label: 'PENDING_CUSTOMERS:STATUS:PENDING' },
  { value: 'APPROVED', label: 'PENDING_CUSTOMERS:STATUS:APPROVED' },
  { value: 'REJECTED', label: 'PENDING_CUSTOMERS:STATUS:REJECTED' },
];

const STATUS_CHIP: Record<
  PendingCustomerStatus,
  { label: string; color: 'default' | 'success' | 'error' }
> = {
  PENDING: {
    label: 'PENDING_CUSTOMERS:STATUS:PENDING',
    color: 'default',
  },
  APPROVED: {
    label: 'PENDING_CUSTOMERS:STATUS:APPROVED',
    color: 'success',
  },
  REJECTED: {
    label: 'PENDING_CUSTOMERS:STATUS:REJECTED',
    color: 'error',
  },
};

/**
 * Pending customers table columns
 */
export const pendingCustomersColumns = [
  {
    title: 'COMMON:COMPANY_NAME',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const isMaxCharacter = customer.customerName?.length > 15;
      return (
        <Tooltip title={isMaxCharacter && customer.customerName} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{customer.customerName}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:CUIT',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const isMaxCharacter = customer.cuit?.length > 11;
      return (
        <Tooltip title={isMaxCharacter && customer.cuit} arrow>
          <Box sx={[styles.rowEllipsis, styles.cuitMaxWidth]}>{customer.cuit}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:SEGMENT',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const isMaxCharacter = customer.companyKey?.length > 10;
      return (
        <Tooltip title={isMaxCharacter && customer.companyKey} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{customer.companyKey || '-'}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:CUSTOMER_TYPE',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const customerTypeLabel = customer.customerType
        ? t(`CLIENTS:DETAIL:${customer.customerType}`)
        : '-';
      const isMaxCharacter = customerTypeLabel.length > 15;
      return (
        <Tooltip title={isMaxCharacter && customerTypeLabel} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{customerTypeLabel}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:STATUS',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const statusProps = STATUS_CHIP[customer.status];
      if (!statusProps) return '-';
      return <Chip {...statusProps} variant='outlined' sx={styles.chip} />;
    },
  },
  {
    title: 'COMMON:CREATED_BY',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const createdBy = customer.createdBy || '-';
      const isMaxCharacter = createdBy.length > 15;
      return (
        <Tooltip title={isMaxCharacter && createdBy} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{createdBy}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:CREATED_AT',
    content: ({ customer }: { customer: PendingCustomer }) => {
      const createdAt = (customer.createdAt && formatDate(customer.createdAt)) ?? '-';
      const isMaxCharacter = createdAt?.length > 15;
      return (
        <Tooltip title={isMaxCharacter && createdAt} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{createdAt}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:ACTIONS',
    content: ({
      customer,
      callback,
    }: {
      customer: PendingCustomer;
      callback: (c: SelectedPendingCustomer) => void;
    }) => <ActionButtons {...{ customer, callback }} />,
    role: [OPERATIONS_SUPERVISOR],
  },
];
